import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/components/AuthProvider';
import {
  getGarmentTypes,
  createGarmentType,
  deleteGarmentType,
  getGarmentFields,
  createGarmentField,
  deleteGarmentField,
} from '@/integrations/supabase/custom-garment-types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { PageHeader } from '@/components/PageHeader';
import { toast } from 'sonner';
import { Plus, Trash2, ChevronDown, ChevronUp, Ruler } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export default function ConfiguracaoTiposPeca() {
  const { empresa, user } = useAuth();
  const queryClient = useQueryClient();
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isTypeDialogOpen, setIsTypeDialogOpen] = useState(false);
  const [isFieldDialogOpen, setIsFieldDialogOpen] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [newField, setNewField] = useState({
    field_label: '',
    field_name: '',
    field_type: 'number' as const,
    unidade: '',
    obrigatorio: false
  });
  const [expandedTypes, setExpandedTypes] = useState<Set<string>>(new Set());

  const { data: garmentTypes = [], isLoading: typesLoading } = useQuery({
    queryKey: ['garment-types', empresa?.id],
    queryFn: () => getGarmentTypes(empresa?.id || ''),
    enabled: !!empresa?.id
  });

  const { data: garmentFields = [], isLoading: fieldsLoading } = useQuery({
    queryKey: ['garment-fields', selectedType],
    queryFn: () => getGarmentFields(empresa?.id || '', selectedType || ''),
    enabled: !!selectedType && !!empresa?.id
  });

  const handleCreateType = async () => {
    if (!newTypeName.trim() || !empresa?.id) return;

    const result = await createGarmentType(empresa.id, newTypeName, user?.id || '');
    if (result.ok) {
      toast.success('Tipo de peça criado com sucesso!');
      setNewTypeName('');
      setIsTypeDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['garment-types'] });
    } else {
      toast.error(result.error || 'Erro ao criar tipo de peça');
    }
  };

  const handleDeleteType = async (id: string) => {
    if (confirm('Tem certeza que deseja deletar este tipo de peça?')) {
      const result = await deleteGarmentType(id);
      if (result.ok) {
        toast.success('Tipo de peça deletado!');
        if (selectedType === id) setSelectedType(null);
        queryClient.invalidateQueries({ queryKey: ['garment-types'] });
      } else {
        toast.error('Erro ao deletar tipo de peça');
      }
    }
  };

  const handleCreateField = async () => {
    if (!selectedType || !newField.field_label.trim() || !empresa?.id) return;

    const fieldName = newField.field_name || newField.field_label.toLowerCase().replace(/\s+/g, '_');

    const result = await createGarmentField(empresa.id, selectedType, {
      ...newField,
      field_name: fieldName,
      ordem: garmentFields.length
    });

    if (result.ok) {
      toast.success('Campo criado com sucesso!');
      setNewField({ field_label: '', field_name: '', field_type: 'number', unidade: '', obrigatorio: false });
      setIsFieldDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ['garment-fields'] });
    } else {
      toast.error('Erro ao criar campo');
    }
  };

  const handleDeleteField = async (id: string) => {
    if (confirm('Tem certeza que deseja deletar este campo?')) {
      const result = await deleteGarmentField(id);
      if (result.ok) {
        toast.success('Campo deletado!');
        queryClient.invalidateQueries({ queryKey: ['garment-fields'] });
      } else {
        toast.error('Erro ao deletar campo');
      }
    }
  };

  const toggleTypeExpanded = (typeId: string) => {
    const newExpanded = new Set(expandedTypes);
    if (newExpanded.has(typeId)) {
      newExpanded.delete(typeId);
    } else {
      newExpanded.add(typeId);
    }
    setExpandedTypes(newExpanded);
    setSelectedType(typeId);
  };

  if (typesLoading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <SidebarTrigger />
          <Ruler className="w-6 h-6 text-purple-600" />
          <h1 className="text-2xl font-bold">Tipos de Peça</h1>
        </div>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Carregando tipos de peça...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <PageHeader
        title="Tipos de Peça"
        description="Gerencie seus tipos e campos de medida customizados"
        icon={<Ruler className="w-6 h-6 text-purple-600" />}
        statusIndicatorsAbove
        rightActions={
          <Dialog open={isTypeDialogOpen} onOpenChange={setIsTypeDialogOpen}>
            <DialogTrigger asChild>
              <Button size="sm" className="text-xs md:text-sm bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 hover:scale-105 transition-all duration-200 text-white font-medium shadow-md">
                <Plus className="w-4 h-4 mr-1 md:mr-2" />
                <span className="hidden md:inline">Novo Tipo</span>
                <span className="md:hidden">Novo</span>
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Criar Novo Tipo de Peça</DialogTitle>
                <DialogDescription>
                  Adicione um novo tipo de peça que seus clientes usam
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Input
                  placeholder="Ex: Bolsa, Maleta, Garrafa"
                  value={newTypeName}
                  onChange={(e) => setNewTypeName(e.target.value)}
                  autoFocus
                />
                <Button onClick={handleCreateType} className="w-full">
                  Criar Tipo de Peça
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        }
      />

      {/* Conteúdo */}
      <div className="p-4 md:p-6">
        {garmentTypes.length === 0 ? (
          <Card className="border-2 border-dashed border-gray-300">
            <CardContent className="pt-6 text-center">
              <p className="text-gray-600 font-medium mb-2">Nenhum tipo de peça criado</p>
              <p className="text-sm text-gray-500">Crie seu primeiro tipo para começar a usar!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {garmentTypes.map((type) => (
              <Card key={type.id} className="border border-gray-200 hover:shadow-md transition-shadow">
                <div
                  className="p-4 md:p-5 cursor-pointer hover:bg-purple-50/30 transition-colors"
                  onClick={() => toggleTypeExpanded(type.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {expandedTypes.has(type.id) ? (
                        <ChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
                      )}
                      <h3 className="font-semibold text-lg text-foreground truncate">{type.nome}</h3>
                      {type.is_predefined && <Badge variant="secondary" className="flex-shrink-0">Padrão</Badge>}
                    </div>
                    {!type.is_predefined && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteType(type.id);
                        }}
                        className="flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Campos do tipo */}
                {expandedTypes.has(type.id) && (
                  <div className="border-t border-gray-200 bg-purple-50/20 p-4 md:p-5 space-y-4">
                    <div className="flex justify-between items-center">
                      <h4 className="font-semibold text-base text-foreground">Campos de Medida</h4>
                      <Dialog open={isFieldDialogOpen && selectedType === type.id} onOpenChange={setIsFieldDialogOpen}>
                        <DialogTrigger asChild onClick={() => setSelectedType(type.id)}>
                          <Button size="sm" variant="outline" className="gap-1 border-purple-300 hover:bg-purple-50">
                            <Plus className="w-3 h-3" />
                            Novo Campo
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Adicionar Campo</DialogTitle>
                            <DialogDescription>
                              Adicione um novo campo de medida para {type.nome}
                            </DialogDescription>
                          </DialogHeader>
                          <div className="space-y-4">
                            <Input
                              placeholder="Rótulo (ex: Altura, Capacidade)"
                              value={newField.field_label}
                              onChange={(e) => setNewField({ ...newField, field_label: e.target.value })}
                              className="border-purple-300"
                            />
                            <Select value={newField.field_type} onValueChange={(value: any) => setNewField({ ...newField, field_type: value })}>
                              <SelectTrigger className="border-purple-300">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="number">Número</SelectItem>
                                <SelectItem value="text">Texto</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              placeholder="Unidade (ex: cm, ml, kg)"
                              value={newField.unidade}
                              onChange={(e) => setNewField({ ...newField, unidade: e.target.value })}
                              className="border-purple-300"
                            />
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                id="obrigatorio"
                                checked={newField.obrigatorio}
                                onChange={(e) => setNewField({ ...newField, obrigatorio: e.target.checked })}
                                className="w-4 h-4 rounded border-purple-300"
                              />
                              <label htmlFor="obrigatorio" className="text-sm font-medium">Campo obrigatório</label>
                            </div>
                            <Button onClick={handleCreateField} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700">
                              Adicionar Campo
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>

                    {/* Campos listados */}
                    {selectedType === type.id && (
                      <div className="space-y-2">
                        {fieldsLoading ? (
                          <p className="text-gray-500 text-sm">Carregando campos...</p>
                        ) : garmentFields.length === 0 ? (
                          <p className="text-gray-500 text-sm">Nenhum campo criado. Crie o primeiro para começar!</p>
                        ) : (
                          garmentFields.map((field) => (
                            <div key={field.id} className="bg-white p-3 rounded border border-gray-200 flex justify-between items-start gap-2 hover:shadow-sm transition-shadow">
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-foreground">{field.field_label}</p>
                                <p className="text-xs text-gray-600">
                                  Tipo: {field.field_type === 'number' ? 'Número' : 'Texto'} | Unidade: {field.unidade || 'nenhuma'}
                                </p>
                                {field.obrigatorio && <Badge variant="outline" className="text-xs mt-1">Obrigatório</Badge>}
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteField(field.id)}
                                className="flex-shrink-0"
                              >
                                <Trash2 className="w-4 h-4 text-red-500" />
                              </Button>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
